import React, { useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';
import type { ColorResult } from 'react-color';
import { Transition } from '@headlessui/react';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';

import Portal from '../../HOC/Portal';
import { useDisclosure } from '../../hooks';

interface ColorPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
  isLabel?: boolean;
}

const ColorPicker = (props: ColorPickerProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [color, setColor] = useState<string>(props.value);

  const handleColorChange = (result: ColorResult) => {
    setColor(result.hex);
    props.onChange && props.onChange(result.hex);
  };

  useEffect(() => {
    if (props.value) {
      setColor(props.value);
    }
  }, [props.value]);

  return (
    <div
      className={cx(
        'inline-flex items-center',
        props?.className && props?.className
      )}
      data-value={color || isOpen ? 'color' : ''}
    >
      {props.placeholder &&
        !color &&
        (!props.isLabel || (props.isLabel && isOpen)) && (
          <span className="absolute left-0 pointer-events-none text-gray-400">
            {props.placeholder}
          </span>
        )}
      <div
        className="w-[40px] h-[14px] rounded-full cursor-pointer"
        style={{ backgroundColor: color }}
        onClick={onOpen}
      />

      <Portal>
        <Transition appear show={isOpen} as={React.Fragment}>
          <div className="fixed inset-0 z-10">
            <div className="fixed inset-0 w-full h-full" onClick={onClose} />
            <div className="flex inset-0 justify-center align-middle items-center min-h-screen overflow-y-auto">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="transform overflow-visible transition-all">
                  <SketchPicker
                    color={color}
                    disableAlpha
                    onChange={handleColorChange}
                    className="z-50 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 !border-none !shadow-sketch-picker !rounded-none"
                  />
                </div>
              </Transition.Child>
            </div>
          </div>
        </Transition>
      </Portal>
    </div>
  );
};

const MemoizedColorPicker = React.memo(ColorPicker, (prevProps, nextProps) =>
  isEqual(prevProps?.value, nextProps?.value)
);

export default MemoizedColorPicker;
